// Per-user UI preferences. Module-level signals so any component can read /
// write without prop drilling. Backed by SQLite via /api/me/prefs.
//
// Add a new preference: register an entry in PREF_DEFS below, then add the
// key to the backend whitelist in internal/api/prefs.go (allowedPrefKeys).
import { createSignal } from 'solid-js';
import { api } from './api.js';
import { syncThemeFromServer } from './theme.js';

// Registry of boolean prefs. Each entry owns its own signal and getter/setter.
// We expose individual named exports for ergonomic call sites
// (gridShowTypes(), setGridShowTypes(v), …) — these are generated from the
// registry below so loadPrefs / resetPrefs don't have to re-list every key.
function defineBoolPref(key, fallback) {
  const [get, set] = createSignal(fallback);
  return {
    key,
    fallback,
    get,
    set: (v) => {
      set(!!v);
      api.put(`/api/me/prefs/${encodeURIComponent(key)}`, { value: String(!!v) }).catch(() => {});
    },
    load: (raw) => set(raw === undefined || raw === null || raw === ''
      ? fallback
      : (raw === 'true' || raw === '1')),
    reset: () => set(fallback),
  };
}

// Numeric pref with min/max clamp. Stored as a string on the server.
function defineNumberPref(key, fallback, min, max) {
  const clamp = (n) => Math.min(max, Math.max(min, n));
  const [get, set] = createSignal(clamp(fallback));
  return {
    key,
    fallback,
    get,
    set: (v) => {
      const n = clamp(Number(v));
      if (!Number.isFinite(n)) return;
      set(n);
      api.put(`/api/me/prefs/${encodeURIComponent(key)}`, { value: String(n) }).catch(() => {});
    },
    load: (raw) => {
      const n = Number(raw);
      set(Number.isFinite(n) ? clamp(n) : fallback);
    },
    reset: () => set(clamp(fallback)),
  };
}

// Free-form string pref (stored verbatim on the server). Used where a scalar
// bool/number can't capture the state — e.g. a JSON-encoded set of collapsed
// connection groups.
function defineStringPref(key, fallback) {
  const [get, set] = createSignal(fallback);
  return {
    key,
    fallback,
    get,
    set: (v) => {
      const s = String(v ?? '');
      set(s);
      api.put(`/api/me/prefs/${encodeURIComponent(key)}`, { value: s }).catch(() => {});
    },
    load: (raw) => set(raw === undefined || raw === null ? fallback : String(raw)),
    reset: () => set(fallback),
  };
}

const PREFS = {
  showTypes:      defineBoolPref('grid.showTypes',       true),
  pinPK:          defineBoolPref('grid.pinPK',           false),
  thousandSep:    defineBoolPref('grid.thousandSep',     false),
  showHidden:     defineBoolPref('grid.showHidden',      false),
  gridLimitEnabled: defineBoolPref('grid.limitEnabled',  true),
  gridRowLimit:     defineNumberPref('grid.rowLimit', 1000, 1, 100000),
  // Rows per "sample"-tagged table when backing up a database (Settings → Backup).
  backupSampleRows: defineNumberPref('backup.sampleRows', 1000, 1, 1000000),
  querySidebar:   defineBoolPref('query.sidebarVisible', true),
  querySound:     defineBoolPref('query.soundNotify',    false),
  // Per-user DEFAULT for a new query tab's "Completion notification" toggle (the
  // feature itself is admin-enabled globally; this only seeds the per-tab state).
  queryNotifyDefault: defineBoolPref('query.notifyDefault', false),
  queryAutosaveResults: defineBoolPref('query.autosaveResults', false),
  querySafeUpdates: defineBoolPref('query.safeUpdates',  true), // legacy — see querySafeMode below
  // Four-tier safe-update spectrum. Replaces the old boolean toggle:
  //   strict   — MySQL SQL_SAFE_UPDATES=1 (server enforces key-OR-LIMIT;
  //              respects the leftmost-prefix rule for composite indexes).
  //   lax-key  — server flag OFF; client refuses UPDATE/DELETE whose WHERE
  //              touches no indexed column AND has no LIMIT (uses cached
  //              CompletionTable.keys — no extra round-trip).
  //   lax-any  — server flag OFF; client refuses ONLY bare UPDATE/DELETE
  //              with no WHERE at all and no LIMIT (existing unsafe-prompt
  //              modal covers this for free).
  //   off      — server flag OFF; no client check (the legacy "Run anyway"
  //              path, now persistable instead of one-shot).
  // Default 'strict' keeps existing users on MySQL's safe behaviour.
  querySafeMode:    defineStringPref('query.safeUpdatesMode', 'strict'),
  queryTxByDefault: defineBoolPref('query.txByDefault',  true),
  queryShowThreadId: defineBoolPref('query.showThreadId', true),
  // Data-grid editing behaviour (table-data + query-result grids).
  //   editInTransaction — true (default): edits stage in a transaction with an
  //     explicit Commit/Rollback; false: each row write auto-commits immediately.
  //   editRequireMode   — true (default): you must click "Edit" before cells are
  //     editable; false: cells are directly editable (no gate).
  editInTransaction: defineBoolPref('edit.inTransaction', true),
  editRequireMode:   defineBoolPref('edit.requireEditMode', true),
  // Security: idle lock + force-logout (both in SECONDS; 0 = disabled). Defaults
  // are off — opt-in from Settings → Security.
  idleLockSeconds:     defineNumberPref('security.idleLockSeconds',    0, 0, 86400),
  forceLogoutSeconds:  defineNumberPref('security.forceLogoutSeconds', 0, 0, 86400),
  editorAutoFormat: defineBoolPref('editor.autoFormat',  false),
  editorLineWrap:   defineBoolPref('editor.lineWrap',    false),
  editorFontSize:   defineNumberPref('editor.fontSize', 13, 9, 28),
  queryEditorSplit: defineNumberPref('query.editorSplit', 0.55, 0.15, 0.85),
  querySidebarWidth: defineNumberPref('query.sidebarWidth', 260, 200, 600),
  fdslEditorSplit: defineNumberPref('fdsl.editorSplit', 0.44, 0.2, 0.8),
  idslEditorSplit: defineNumberPref('idsl.editorSplit', 0.5, 0.2, 0.8),
  browserSidebarWidth: defineNumberPref('browser.sidebarWidth', 280, 180, 600),
  browserSidebarCollapsed: defineBoolPref('browser.sidebarCollapsed', false),
  connListView:      defineBoolPref('connections.listView', false),
  connCollapsedGroups: defineStringPref('connections.collapsedGroups', '[]'),
};

// Public reactive accessors (matches the previous module-level signals).
export const gridShowTypes      = PREFS.showTypes.get;
export const gridPinPK          = PREFS.pinPK.get;
export const gridThousandSep    = PREFS.thousandSep.get;
export const gridShowHidden     = PREFS.showHidden.get;
export const gridLimitEnabled   = PREFS.gridLimitEnabled.get;
export const gridRowLimit       = PREFS.gridRowLimit.get;
export const backupSampleRows   = PREFS.backupSampleRows.get;
export const querySidebarVisible = PREFS.querySidebar.get;
export const querySoundNotify   = PREFS.querySound.get;
export const queryNotifyDefault    = PREFS.queryNotifyDefault.get;
export const setQueryNotifyDefault = PREFS.queryNotifyDefault.set;
export const queryAutosaveResults    = PREFS.queryAutosaveResults.get;
export const setQueryAutosaveResults = PREFS.queryAutosaveResults.set;
export const querySafeUpdates   = PREFS.querySafeUpdates.get;
// Tri/quad-state — 'strict' | 'lax-key' | 'lax-any' | 'off'. On first load (no
// server-saved value) the getter falls back to the OLD boolean: bool true →
// 'strict', bool false → 'off'. That way existing users transition cleanly.
export const querySafeMode = () => {
  const v = PREFS.querySafeMode.get();
  if (v === 'strict' || v === 'lax-key' || v === 'lax-any' || v === 'off') return v;
  return PREFS.querySafeUpdates.get() ? 'strict' : 'off';
};
export const queryTxByDefault   = PREFS.queryTxByDefault.get;
export const editInTransaction    = PREFS.editInTransaction.get;
export const setEditInTransaction = PREFS.editInTransaction.set;
export const editRequireMode      = PREFS.editRequireMode.get;
export const setEditRequireMode   = PREFS.editRequireMode.set;
export const queryShowThreadId  = PREFS.queryShowThreadId.get;
export const idleLockSeconds      = PREFS.idleLockSeconds.get;
export const forceLogoutSeconds   = PREFS.forceLogoutSeconds.get;
export const editorAutoFormat   = PREFS.editorAutoFormat.get;
export const editorLineWrap     = PREFS.editorLineWrap.get;
export const editorFontSize     = PREFS.editorFontSize.get;
export const queryEditorSplit   = PREFS.queryEditorSplit.get;
export const querySidebarWidth  = PREFS.querySidebarWidth.get;
export const fdslEditorSplit    = PREFS.fdslEditorSplit.get;
export const idslEditorSplit    = PREFS.idslEditorSplit.get;
export const browserSidebarWidth = PREFS.browserSidebarWidth.get;
export const browserSidebarCollapsed = PREFS.browserSidebarCollapsed.get;
export const connectionsListView = PREFS.connListView.get;
export const connectionsCollapsedGroups = PREFS.connCollapsedGroups.get;

// Public setters — flip signal immediately, fire-and-forget save.
export const setGridShowTypes      = PREFS.showTypes.set;
export const setGridPinPK          = PREFS.pinPK.set;
export const setGridThousandSep    = PREFS.thousandSep.set;
export const setGridShowHidden     = PREFS.showHidden.set;
export const setGridLimitEnabled   = PREFS.gridLimitEnabled.set;
export const setGridRowLimit       = PREFS.gridRowLimit.set;
export const setBackupSampleRows   = PREFS.backupSampleRows.set;
export const setQuerySidebarVisible = PREFS.querySidebar.set;
export const setQuerySoundNotify   = PREFS.querySound.set;
export const setQuerySafeUpdates   = PREFS.querySafeUpdates.set;
export const setQuerySafeMode      = (v) => {
  // Setting the new mode also syncs the legacy bool so old read sites get
  // sane behaviour during the transition (strict → true; everything else → false).
  PREFS.querySafeMode.set(v);
  PREFS.querySafeUpdates.set(v === 'strict');
};
export const setQueryTxByDefault   = PREFS.queryTxByDefault.set;
export const setQueryShowThreadId  = PREFS.queryShowThreadId.set;
export const setIdleLockSeconds      = PREFS.idleLockSeconds.set;
export const setForceLogoutSeconds   = PREFS.forceLogoutSeconds.set;
export const setEditorAutoFormat   = PREFS.editorAutoFormat.set;
export const setEditorLineWrap     = PREFS.editorLineWrap.set;
export const setEditorFontSize     = PREFS.editorFontSize.set;
export const setQueryEditorSplit   = PREFS.queryEditorSplit.set;
export const setQuerySidebarWidth  = PREFS.querySidebarWidth.set;
export const setFdslEditorSplit    = PREFS.fdslEditorSplit.set;
export const setIdslEditorSplit    = PREFS.idslEditorSplit.set;
export const setBrowserSidebarWidth = PREFS.browserSidebarWidth.set;
export const setBrowserSidebarCollapsed = PREFS.browserSidebarCollapsed.set;
export const setConnectionsListView = PREFS.connListView.set;
export const setConnectionsCollapsedGroups = PREFS.connCollapsedGroups.set;

// Fetch all prefs from the server and populate the local signals.
// Safe to call multiple times; idempotent.
export async function loadPrefs() {
  try {
    const r = await api.get('/api/me/prefs');
    const raw = r.prefs || {};
    for (const p of Object.values(PREFS)) p.load(raw[p.key]);
    // Theme lives in its own module (applied synchronously at boot from a
    // localStorage cache); reconcile the per-account value now it's loaded.
    syncThemeFromServer(raw['ui.theme']);
  } catch {
    // Silent — fall back to defaults already in the signals.
  }
}

// Reset signals to defaults — call on logout so the next user doesn't see
// the previous user's toggle state.
export function resetPrefs() {
  for (const p of Object.values(PREFS)) p.reset();
}
